const DB_NAME = '_r';
const DB_VERSION = 1;
const STORES = ['chapters', 'chunks', 'timing', 'meta'] as const;
type StoreName = (typeof STORES)[number];

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    // Delete old FCBH-named DB on first open — it held only re-fetchable API cache.
    if ('databases' in indexedDB) {
      void (indexedDB as IDBFactory & { databases(): Promise<{ name?: string }[]> })
        .databases()
        .then((dbs) => {
          if (dbs.some((d) => d.name === 'fcbh-reader')) {
            indexedDB.deleteDatabase('fcbh-reader');
          }
        })
        .catch(() => {});
    } else {
      (indexedDB as IDBFactory).deleteDatabase('fcbh-reader');
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      for (const name of STORES) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
      }
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void => reject(req.error);
  });
  return dbPromise;
}

export async function idbGet<T>(store: StoreName, key: string): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(key);
    req.onsuccess = (): void => resolve(req.result as T | undefined);
    req.onerror = (): void => reject(req.error);
  });
}

export async function idbPut<T>(store: StoreName, key: string, value: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(value, key);
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => reject(tx.error);
  });
}

export async function idbDelete(store: StoreName, key: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).delete(key);
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => reject(tx.error);
  });
}
